// Global Instructions Rule Applied!
import React, { useMemo, useCallback, useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { UsergroupAddOutlined } from '@ant-design/icons'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../../../../core/utils/errorToast.tsx'
import { KanbanBoard } from '../../../../../../core/components/KanbanBoard/index.tsx'
import { KanbanColumn, ColumnHeaderConfig } from '../../../../../../core/components/KanbanBoard/interfaces.ts'
import { useFundraisingManager } from '../../../../hooks/useFundraisingManager.ts'
import { useFundraisingPipelineData } from '../../../../hooks/useFundraisingPipelineData.ts'
import { useFundraisingPipelineDrawer } from '../../../../hooks/useFundraisingPipelineDrawer.ts'
import { useTabManagerContext } from '../../../../../../core/app-layout/lib/TabManagerContext.tsx'
import { getBackendBaseUrl } from '../../../../../../core/utils/endpoints.ts'
import { API_ROUTES } from '../../../../../../core/utils/endpoints.ts'
import { getAccessToken } from '../../../../../../core/auth/authService'
import { store } from '../../../../../../state_manager/store.ts'
import { PipelineStageEntryFields } from '../../../../../../state_manager/data_models/pipeline_stage_entry.ts'
import { useCapabilityCheck } from '../../../../../../core/hooks/useCapability.ts'
import { CAPABILITY_PIPELINE_STAGE_MOVE } from '../../../../../../core/hooks/useStageMoveRights.ts'
import { ProspectRecordFields } from '../../../../../../state_manager/data_models/prospect_record.ts'
import { sortItemsByPriority } from '../../../../../../core/components/KanbanBoard/controller/parts/columnUtils.ts'
import { planColumnMove } from '../../../../../../core/components/KanbanBoard/controller/parts/priorityUtils.ts'
import {
  TABLE_PROSPECTS,
  TABLE_PIPELINE_PROSPECTS,
  VIEW_TYPE_KANBAN_BOARD,
  MENU_ITEM_KEY_FUNDRAISING_PIPELINE
} from '../../../../../../state_manager/globals.ts'
import { MESSAGE_UPDATE_SUCCESSFUL } from '../../../../../../state_manager/constants/strings.ts'
import { ProspectKanbanItem } from './interfaces.ts'
import type { ProspectRecord } from '../../../../../../state_manager/interfaces/fundraising.ts'
import {
  updateProspectRecord,
  setPipelineSelectedFund
} from '../../../../../../state_manager/state_update_actions/fundraising/index.ts'
import { useMenuItemUserPreference } from '../../../../../../app_modules/global_configs/useMenuItemUserPreference.ts'
import type { AppDispatch } from '../../../../../../state_manager/store.ts'

import Button from '../../../../../../core/components/Button.jsx'
import ViewToolbar from '../../../../../../core/components/ViewToolbar/index.tsx'
import { useFundraisingCardConfig } from '../../../../../../core/components/KanbanBoard/components/cards/Fundraising.tsx'
import { useCurrencies } from '../../../../../../app_modules/currencies/useCurrencies.ts'
import PipelineFundSelect from './PipelineFundSelect.tsx'
import { VIEW_TASK_CONFIG } from '../../../../../../ui_configs/fund_management/view_task_config.ts'
import type { FundsModel } from '../../../../../../state_manager/data_models/funds_model.ts'

const FundraisingKanban: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const { openFormInTab } = useTabManagerContext()
  const [successfullyMovedCardId, setSuccessfullyMovedCardId] = useState<string | null>(null)

  const reduxSelectedFundId = useSelector(
    (state: { fundraising?: { pipeline?: { selectedFundId?: string | null } } }) =>
      state.fundraising?.pipeline?.selectedFundId ?? null
  )
  const { menuItemId: fundraisingPipelineMenuItemId, preferenceRow, menuItems, persistPreference } =
    useMenuItemUserPreference(MENU_ITEM_KEY_FUNDRAISING_PIPELINE)

  // The board's default fund comes from cfg (app_config
  // fundraising.default_pipeline_fund_id), not a hardcoded uuid — that literal
  // was environment-specific and meaningless on UAT. NULL is valid: the picker
  // then opens empty and the user chooses.
  const defaultFundId = useSelector(
    (state: { globalConfigs?: { fundraising_default_fund_id?: string | null } }) =>
      state.globalConfigs?.fundraising_default_fund_id ?? null
  )

  const effectiveSelectedFundId = useMemo(() => {
    if (reduxSelectedFundId !== null && reduxSelectedFundId !== undefined) return reduxSelectedFundId
    const saved = preferenceRow?.report_id ?? null
    return saved ?? defaultFundId
  }, [reduxSelectedFundId, preferenceRow?.report_id, defaultFundId])

  const selectedFundId = effectiveSelectedFundId

  const funds = useSelector(
    (state: { globalConfigs?: { funds?: FundsModel[] } }) => state.globalConfigs?.funds || []
  )
  const selectedFund = useMemo(
    () => funds.find((f) => f.id === selectedFundId),
    [funds, selectedFundId]
  )

  const { defaultPipelineId, getPipelineStages } = useFundraisingManager()
  const kanbanPipelineId = useMemo(() => {
    const po = selectedFund?.pipeline_origination
    if (typeof po === 'string' && po.length > 0) return po
    return defaultPipelineId ?? ''
  }, [selectedFund, defaultPipelineId])

  const pipelineStages: ReturnType<typeof getPipelineStages> = useMemo(
    () => getPipelineStages(kanbanPipelineId, VIEW_TYPE_KANBAN_BOARD),
    [getPipelineStages, kanbanPipelineId]
  )

  const {
    pipelineProspectsData: prospects,
    pipelineProspectsLoading: prospectsLoading,
    handleFetchPipelineProspects: handleRefreshPipeline
  } = useFundraisingPipelineData(effectiveSelectedFundId)

  useEffect(() => {
    if ((reduxSelectedFundId === null || reduxSelectedFundId === undefined) && menuItems.length > 0) {
      const initialFundId = preferenceRow?.report_id ?? defaultFundId
      if (initialFundId) dispatch(setPipelineSelectedFund({ selectedFundId: initialFundId }))
    }
  }, [dispatch, reduxSelectedFundId, preferenceRow?.report_id, menuItems.length, defaultFundId])

  const { handleOpenDrawerForProspect, handleTasksClick, handleTaskToggle } =
    useFundraisingPipelineDrawer(prospects)

  const { formatAmountById } = useCurrencies()

  const pipelineConfigs = useSelector(
    (state: { globalConfigs?: { pipeline_configs?: unknown[] } }) => state.globalConfigs?.pipeline_configs || []
  )
  const allPipelineStages = useSelector(
    (state: { globalConfigs?: { pipeline_stages?: unknown[] } }) => state.globalConfigs?.pipeline_stages || []
  )

  // Who may move prospects into or out of each stage (pipeline_stage.move): a
  // column the user may not move into or out of is read-only for them, with
  // the reason on its marker. This replaced the board-only drop_option_kb switch.
  const canMoveStage = useCapabilityCheck()

  const columns: KanbanColumn[] = useMemo(() => {
    if (!pipelineStages.length) return []

    return pipelineStages.map((stage) => {
      const id = stage[PipelineStageEntryFields.id] as string
      const name = (stage[PipelineStageEntryFields.prog__name] as string) || ''
      const move = canMoveStage(CAPABILITY_PIPELINE_STAGE_MOVE, id, name)
      return {
        id,
        name,
        sequence: (stage[PipelineStageEntryFields.sequence_kb] as number | null | undefined) ?? null,
        visible: stage[PipelineStageEntryFields.visible_kb] as boolean,
        dropOption: move.allowed,
        readOnlyReason: move.reason,
        colour: (stage[PipelineStageEntryFields.colour] as string | null | undefined) || undefined
      }
    })
  }, [pipelineStages, canMoveStage])

  const kanbanItems: ProspectKanbanItem[] = useMemo(() => {
    if (!prospects || !prospects.length) return []

    return prospects.map((prospect: ProspectRecord) => {
      const prospectId = prospect.id || (prospect as any)[ProspectRecordFields.prospects__id] || ''
      const columnId =
        (prospect as any)[ProspectRecordFields.pipeline_prospects__stage] ||
        (prospect as any)[ProspectRecordFields.pipeline_config__id] ||
        ''

      return {
        ...prospect,
        id: prospectId,
        columnId,
        [ProspectRecordFields.prog__tasks]: (prospect as any)[ProspectRecordFields.prog__tasks] || []
      }
    })
  }, [prospects])

  const sortedItems: ProspectKanbanItem[] = useMemo(() => {
    const itemsByColumn = new Map<string, ProspectKanbanItem[]>()

    kanbanItems.forEach((item) => {
      const columnId = item.columnId || ''
      if (!itemsByColumn.has(columnId)) {
        itemsByColumn.set(columnId, [])
      }
      itemsByColumn.get(columnId)?.push(item)
    })

    const sorted: ProspectKanbanItem[] = []
    columns.forEach((column) => {
      const columnItems = itemsByColumn.get(column.id) || []
      const sortedColumnItems = sortItemsByPriority(
        columnItems,
        (item) => (item as any)[ProspectRecordFields.pipeline_prospects__sequence] as number | null | undefined
      )
      sorted.push(...sortedColumnItems)
    })

    return sorted
  }, [kanbanItems, columns])

  const handleInvestorNameClick = useCallback(
    (e: React.MouseEvent, accountId: string, accountName: string) => {
      e.stopPropagation()
      if (accountId) {
        openFormInTab('companies', accountId, accountName)
      }
    },
    [openFormInTab]
  )

  const handlePatchSuccess = useCallback(
    (recordId: string, updates: Record<string, unknown>): void => {
      dispatch(updateProspectRecord({ prospectId: recordId, updates }))
    },
    [dispatch]
  )

  const handleFundChange = useCallback(
    (fundId: string | number | null) => {
      const newFundId = fundId != null && fundId !== '' ? String(fundId) : null
      dispatch(setPipelineSelectedFund({ selectedFundId: newFundId }))
      if (fundraisingPipelineMenuItemId && newFundId) {
        persistPreference(newFundId, {}).catch((err) => console.error('Failed to persist fund preference:', err))
      }
    },
    [dispatch, fundraisingPipelineMenuItemId, persistPreference]
  )

  const cardConfig = useFundraisingCardConfig({
    handleOpenDrawerForProspect,
    handleTasksClick,
    handleTaskToggle,
    handleInvestorNameClick,
    handlePatchSuccess,
    taskConfig: VIEW_TASK_CONFIG[MENU_ITEM_KEY_FUNDRAISING_PIPELINE]
  })

  const handleMoveProspect = useCallback(
    async (prospectId: string, fromColumnId: string, toColumnId: string, newIndex: number): Promise<void> => {
      const currentToken = getAccessToken(store.getState().auth)
      if (!currentToken) return

      try {
        const movedItem = sortedItems.find((item) => item.id === prospectId)
        if (!movedItem) return

        // Plan against the target column as displayed: for a same-column drag
        // the moved card is still in it and the DropZone index counts it.
        const targetColumnItems = sortedItems.filter((item) => item.columnId === toColumnId)
        const plan = planColumnMove(
          targetColumnItems,
          movedItem,
          newIndex,
          (item) => item[ProspectRecordFields.pipeline_prospects__sequence]
        )

        // Dropped into its own slot — nothing changes
        if (plan.sequence === null) return
        const newSequence = plan.sequence

        const pipelineProspectsId = movedItem[ProspectRecordFields.pipeline_prospects__id]
        if (!pipelineProspectsId) {
          showErrorToast('Pipeline prospects record not found for this prospect')
          return
        }

        const apiUpdates: Record<string, unknown> = {
          id: pipelineProspectsId,
          sequence: newSequence
        }

        if (fromColumnId !== toColumnId) {
          apiUpdates.stage = toColumnId
          apiUpdates.stage_date = new Date().toISOString().split('T')[0]
        }

        const baseUrl = getBackendBaseUrl()
        const url = `${baseUrl}${API_ROUTES.FUNDRAISING.PIPELINE_PROSPECTS.path}`

        const response = await fetch(url, {
          method: API_ROUTES.FUNDRAISING.PIPELINE_PROSPECTS.method,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${currentToken}`
          },
          body: JSON.stringify(apiUpdates)
        })

        if (!response.ok) {
          let errorMessage = `Failed to move prospect: ${response.status} ${response.statusText}`
          try {
            const errorData = await response.json()
            if (errorData.message) errorMessage = errorData.message
            else if (errorData.error) errorMessage = errorData.error
          } catch {
            // ignore
          }
          showErrorToast(errorMessage)
        } else {
          // Neighbours renumbered so the column order is unambiguous after the drop
          const renumberResponses = await Promise.all(
            plan.renumbered.map(({ item, sequence }) =>
              fetch(url, {
                method: API_ROUTES.FUNDRAISING.PIPELINE_PROSPECTS.method,
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${currentToken}`
                },
                body: JSON.stringify({ id: item[ProspectRecordFields.pipeline_prospects__id], sequence })
              })
            )
          )
          if (renumberResponses.some((r) => !r.ok)) {
            showErrorToast('Failed to renumber the column — reloading the pipeline')
            handleRefreshPipeline()
            return
          }

          // Update Redux store locally instead of refetching everything
          const reduxUpdates: Record<string, unknown> = {
            [ProspectRecordFields.pipeline_prospects__sequence]: newSequence
          }
          if (fromColumnId !== toColumnId) {
            const stageDate = new Date().toISOString().split('T')[0]
            reduxUpdates[ProspectRecordFields.pipeline_prospects__stage] = toColumnId
            reduxUpdates[ProspectRecordFields.pipeline_prospects__stage_date] = stageDate
            reduxUpdates[ProspectRecordFields.pipeline_config__id] = toColumnId
            const config = (pipelineConfigs as Array<{ id?: string; stage?: string }>).find(
              (c) => c.id === toColumnId
            )
            if (config) {
              const stage = (allPipelineStages as Array<{ id?: string; name?: string | null }>).find(
                (s) => s.id === config.stage
              )
              if (stage?.name) {
                reduxUpdates[ProspectRecordFields.pipeline_prospects__stage__label] = stage.name
              }
            }
          }
          dispatch(updateProspectRecord({ prospectId, updates: reduxUpdates }))
          plan.renumbered.forEach(({ item, sequence }) => {
            dispatch(
              updateProspectRecord({
                prospectId: item.id,
                updates: { [ProspectRecordFields.pipeline_prospects__sequence]: sequence }
              })
            )
          })
          toast.success(MESSAGE_UPDATE_SUCCESSFUL)
          setSuccessfullyMovedCardId(prospectId)
          setTimeout(() => setSuccessfullyMovedCardId(null), 1200)
        }
      } catch (error) {
        showErrorToast('Failed to move prospect')
        console.error('Error moving prospect:', error)
      }
    },
    [sortedItems, dispatch, pipelineConfigs, allPipelineStages, handleRefreshPipeline]
  )

  const handleProspectUpdate = useCallback(
    async (_prospectId: string, _fieldName: string, _fieldValue: unknown): Promise<void> => {
      handleRefreshPipeline()
    },
    [handleRefreshPipeline]
  )

  const columnHeaderConfig: ColumnHeaderConfig<ProspectKanbanItem> = useMemo(
    () => ({
      renderCount: (count: number) => (
        <span className='text-xs font-medium' style={{ color: '#6b7280' }}>
          {count} prospects
        </span>
      ),
      renderTotal: (total: string | number, column) => {
        const columnProspects = sortedItems.filter((item) => item.columnId === column.id)
        let columnTotal = 0
        let hasValidAmounts = false
        let primaryCurrency: string | null = null

        columnProspects.forEach((prospect) => {
          const commitmentAmount = prospect[ProspectRecordFields.prospects__commitment] as number | null | undefined
          if (commitmentAmount && typeof commitmentAmount === 'number') {
            columnTotal += commitmentAmount
            hasValidAmounts = true
            if (!primaryCurrency)
              primaryCurrency =
                (prospect[ProspectRecordFields.prospects__currency] as string | null) || null
          }
        })

        if (!hasValidAmounts) return null

        const currencyToUse = selectedFund?.functional_currency_id ?? primaryCurrency ?? ''
        const formattedAmount = formatAmountById(columnTotal, currencyToUse)

        return <span className='text-xs'>{formattedAmount} committed</span>
      }
    }),
    [sortedItems, formatAmountById, selectedFund]
  )

  const loading = prospectsLoading

  return (
    <div className='h-full'>
      <div className='w-full h-full flex flex-col bg-white pl-2 pr-4 pb-4'>
        <div className='h-1 bg-white'></div>

        <ViewToolbar
          moduleKey={TABLE_PROSPECTS}
          subModuleKey={TABLE_PIPELINE_PROSPECTS}
          recordTitleField={ProspectRecordFields.accounts__name}
          onRefreshData={() => handleRefreshPipeline()}
          addButtonNavigateState={{ selectedFundId: selectedFundId ?? defaultFundId }}
          leftSlot={
            <PipelineFundSelect
              value={selectedFundId}
              onChange={handleFundChange}
            />
          }
          beforeAddButtonSlot={
            <Button icon={<UsergroupAddOutlined />} onClick={() => navigate('/bulk-add-prospects')}>
              Bulk Prospects
            </Button>
          }
        />

        <KanbanBoard
            columns={columns}
            items={sortedItems}
            cardConfig={cardConfig}
            columnHeaderConfig={columnHeaderConfig}
            onMoveItem={handleMoveProspect}
            onUpdateItem={handleProspectUpdate}
            loading={loading}
            emptyState={{
              title: 'No pipeline stages found',
              action: (
                <Button variant='primary' onClick={() => handleRefreshPipeline()}>
                  Reload Pipeline
                </Button>
              )
            }}
            getColumnId={(item) => item.columnId}
            successfullyMovedCardId={successfullyMovedCardId}
          />
      </div>
    </div>
  )
}

export default FundraisingKanban
